import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import App from './App';
import useSimulationStore from './store/simulationStore';

export const AppLoader: React.FC = (): React.ReactElement | null => {
    // Reference to track previous loading state
    const prevChemistryLoadingRef = useRef<boolean>(true); 
    const hasAutoFittedRef = useRef<boolean>(false);

    // --- Trigger Initial Data Loads ---
    useEffect(() => {
        //console.log("[AppLoader] Triggering initial data loads...");
        const { loadElementProperties, loadChemistryManifests } = useSimulationStore.getState();
        loadElementProperties();
        loadChemistryManifests(); 
    }, []);
    
    // --- Auto-fit reactor to viewport on initial load ---
    useEffect(() => {
        if (!hasAutoFittedRef.current) {
            hasAutoFittedRef.current = true;
            const state = useSimulationStore.getState();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const cameraDistance = 50; // Initial distance, CameraPositioner will adjust
            console.log('[AppLoader] Auto-fitting reactor to viewport on initial load');
            state.fitReactorToViewport(viewportWidth, viewportHeight, cameraDistance, state.cameraFov, state.controls.is2DMode);
        }
    }, []);

    // --- Subscribe to Loading and Error States (include reaction defs) ---
    const {
        isLoadingSpeciesManifest,
        isLoadingReactionsManifest,
        isLoadingReactionDefinitions,
        errorLoadingChemistryData,
    } = useSimulationStore(state => ({
        isLoadingSpeciesManifest: state.isLoadingSpeciesManifest,
        isLoadingReactionsManifest: state.isLoadingReactionsManifest,
        isLoadingReactionDefinitions: state.isLoadingReactionDefinitions,
        errorLoadingChemistryData: state.errorLoadingChemistryData,
    }));

    const isChemistryLoading = isLoadingSpeciesManifest || isLoadingReactionsManifest || isLoadingReactionDefinitions;

    // --- Effect to Trigger Geometry Update After Load --- 
    useEffect(() => {
        const wasLoading = prevChemistryLoadingRef.current;
        // Check if loading just finished (transitioned from true to false)
        if (wasLoading && !isChemistryLoading && !errorLoadingChemistryData) {
            //console.log("[AppLoader] Initial chemistry load complete. Triggering geometry update...");
            useSimulationStore.getState().updateSpeciesGeometry(); // Call the new action
        }
        // Update previous state ref for next render
        prevChemistryLoadingRef.current = isChemistryLoading;

    }, [isChemistryLoading, errorLoadingChemistryData]); // Run when loading/error state changes

    //console.log(`[AppLoader] Render check. isChemistryLoading: ${isChemistryLoading}, error: ${errorLoadingChemistryData}`);

    // --- Determine Content Based on State ---
    let content: React.ReactNode = null; // Initialize content

    if (errorLoadingChemistryData) {
        content = (
            <Paper elevation={3} sx={{ padding: 4, margin: 4, borderColor: 'error.main', borderWidth: 2, borderStyle: 'solid' }}>
                <Typography variant="h5" color="error" gutterBottom>Application Load Error</Typography>
                <Typography color="error">Failed to load essential chemistry data:</Typography>
                <Typography sx={{ mt: 1, fontFamily: 'monospace', color: 'error.dark' }}>{errorLoadingChemistryData}</Typography>
                <Typography sx={{ mt: 2 }}>Please check the console and network tabs for more details, and ensure the data files (manifests, definitions) are accessible in the public folder.</Typography>
            </Paper>
        );
    } else if (isChemistryLoading) { 
        // @ts-ignore - Suppressing complex union type error for this specific Box structure
        content = (
            <Box component="div" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography>Loading Chemistry Definitions...</Typography>
            </Box>
        );
    } else {
        // --- Loading Complete: Set App as content ---
        //console.log("[AppLoader] Loading complete, rendering App.");
        content = <App />;
    }

    // Cast the final content to the expected return type
    return content as React.ReactElement | null;
};

// No default export needed if it's only used in index.tsx
// export default AppLoader; // Keep commented unless needed elsewhere 